import type { BrandSkin } from "@calcom/features/teams/lib/brandSkins";

/** Logo de la marque (monogramme CCC bleu, carré Launchpad), sur les deux marques. */
export function BrandLogo(props: { skin: BrandSkin; className?: string }) {
  return (
    <img
      src={props.skin.markUrl}
      alt={props.skin.name}
      width={64}
      height={64}
      className={props.className ?? "h-16 w-16"}
      data-testid="brand-logo"
    />
  );
}

export const framedBookerClassNames = {
  bookerWrapper: "!my-0 w-full",
  bookerContainer: [
    "!rounded-2xl !border-0 shadow-none",
    // 1024 à 1279 px : les 3 colonnes de cal (280 + 480 + 280) débordaient du cadre et coupaient les créneaux.
    "lg:max-xl:![--booker-meta-width:260px] lg:max-xl:![--booker-main-width:440px] lg:max-xl:![--booker-timeslots-width:260px]",
    // Étape formulaire : le booker (340 + 420) flottait au milieu du cadre de 1040 px. Il le remplit.
    "xl:has-[form]:![--booker-main-width:700px] lg:max-xl:has-[form]:![--booker-main-width:620px]",
  ].join(" "),
};

/** Match the booker (~1040px) or the profile list (max-w-3xl / 768px). */
export type BrandFrameWidth = "booker" | "profile";

const WIDTH_CLASS: Record<BrandFrameWidth, string> = {
  booker: "max-w-[1040px]",
  profile: "max-w-3xl",
};

export function BrandFrame(props: {
  skin: BrandSkin;
  /** Content-aligned card width. Booker pages vs profile lists. */
  width: BrandFrameWidth;
  eyebrow?: string;
  meta?: string;
  children: React.ReactNode;
}) {
  const widthClass = WIDTH_CLASS[props.width];

  return (
    <div
      className="min-h-screen bg-[#F4F4F5] px-4 py-6 sm:px-8 sm:py-8"
      style={{ color: props.skin.ink }}
      data-testid="brand-frame"
      data-brand={props.skin.slug}
      data-width={props.width}>
      {props.eyebrow || props.meta ? (
        <div
          className={`mx-auto mb-4 flex w-full ${widthClass} flex-col items-start gap-1 pr-0 text-sm font-medium md:pr-0 lg:pr-72`}
          data-testid="brand-frame-header">
          {props.eyebrow ? (
            <span className="flex min-w-0 max-w-full items-center gap-2">
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: props.skin.brandColor }}
                data-testid="brand-bar"
              />
              <span className="truncate">{props.eyebrow}</span>
            </span>
          ) : null}
          {props.meta ? (
            <span className="max-w-full truncate pl-4" data-testid="brand-frame-meta">
              {props.meta}
            </span>
          ) : null}
        </div>
      ) : null}
      <div
        className={`mx-auto w-full ${widthClass} overflow-hidden rounded-2xl bg-white text-[#1C1A17] shadow-sm`}
        data-testid="brand-frame-card">
        {props.children}
      </div>
    </div>
  );
}
